import React from "react";
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const copper = "#B87333";
const copperDark = "#935C29";

function modeLabel(decision) {
  const raw = String(decision?.interaction_mode || decision?.decision_type || "decision").replace(/_/g, " ");
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function iconFor(decision) {
  const mode = decision?.interaction_mode;
  if (mode === "artifact_lookup") return <Inventory2OutlinedIcon fontSize="small" />;
  if (mode === "catalog_discovery") return <MenuBookIcon fontSize="small" />;
  if (mode === "diagnostics") return <TravelExploreIcon fontSize="small" />;
  if (mode === "setup_or_admin_action") return <VpnKeyIcon fontSize="small" />;
  if (decision?.decision_type === "clarification_required") return <WarningAmberIcon fontSize="small" />;
  return <MenuBookIcon fontSize="small" />;
}

export default function CasperInteractionDecisionCard({ decision, onUseResource }) {
  const theme = useTheme();
  if (!decision) return null;
  const isDark = theme.palette.mode === "dark";
  const links = Array.isArray(decision.links) ? decision.links : [];
  const entities = Array.isArray(decision.selected_entities) ? decision.selected_entities : [];
  const surface = isDark ? alpha("#241B15", 0.96) : "rgba(255,252,249,0.96)";
  const border = isDark ? "rgba(224,177,132,0.32)" : "rgba(184,115,51,0.24)";
  const title = decision.title || modeLabel(decision);
  const message = decision.message || "";

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.2,
        p: { xs: 1.35, md: 1.55 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: border,
        bgcolor: surface,
      }}
    >
      <Stack spacing={1.15}>
        <Stack direction="row" spacing={1.1} alignItems="flex-start">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: isDark ? alpha(copper, 0.14) : "rgba(184,115,51,0.09)",
              color: isDark ? "#F2C69B" : copperDark,
              flex: "0 0 auto",
            }}
          >
            {iconFor(decision)}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={950}>
                {title}
              </Typography>
              <Chip
                size="small"
                label={modeLabel(decision)}
                sx={{
                  height: 22,
                  fontWeight: 850,
                  bgcolor: isDark ? alpha(copper, 0.12) : "rgba(184,115,51,0.08)",
                  color: isDark ? "#F2C69B" : copperDark,
                }}
              />
            </Stack>
            {message && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                {message}
              </Typography>
            )}
          </Box>
        </Stack>

        {links.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {links.map((link) => (
              <Button
                key={`${link.label}-${link.href}`}
                component={Link}
                href={link.href}
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon fontSize="inherit" />}
                sx={{ borderColor: border, color: isDark ? "#F2C69B" : copperDark, fontWeight: 850 }}
              >
                {link.label || "Open"}
              </Button>
            ))}
          </Stack>
        )}

        {entities.length > 0 && (
          <Stack spacing={0.75}>
            {entities.slice(0, 5).map((entity) => (
              <Box
                key={`${entity.type || "resource"}-${entity.id || entity.name}`}
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: isDark ? alpha("#FFFFFF", 0.07) : "rgba(184,115,51,0.12)",
                  bgcolor: isDark ? alpha("#FFFFFF", 0.04) : "rgba(255,255,255,0.68)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={900}>
                      {entity.display_name || entity.name || entity.id}
                    </Typography>
                    {entity.description && (
                      <Typography variant="caption" color="text.secondary">
                        {entity.description}
                      </Typography>
                    )}
                  </Box>
                  {onUseResource && (
                    <Button size="small" variant="text" onClick={() => onUseResource(entity)} sx={{ fontWeight: 900 }}>
                      Use this
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
